/**
 * PrepaymentModal (public-booking) — REAL upfront charge for a NO-SHOW-BLOCKED guest (Jose 2026-10-25).
 *
 * Ported from the customer-portal version so a guest auto-blocked after two no-shows can pay their
 * appointment IN FULL on the public site (previously they could only e-sign, with no payment proof).
 * Create a Stripe PaymentIntent (the existing `createPaymentIntent` CF — enables Apple Pay / Google Pay),
 * render Stripe's PaymentElement, and only on a CONFIRMED charge call onSuccess(paymentIntentId). The
 * caller then re-books with the payment proof so the server's no-show gate lets it through.
 *
 * Project-aware URL → staging hits staging functions (Stripe TEST), prod hits LIVE.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, X, Shield, CreditCard, AlertTriangle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements as StripeElements,
  PaymentElement as StripePaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';

const Elements = StripeElements as any;
const PaymentElement = StripePaymentElement as any;

const PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'slik-pos-barberlegacy';
const FUNCTIONS_URL = `https://us-central1-${PROJECT_ID}.cloudfunctions.net`;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

interface Props {
  amount: number;            // total due now (service price in full)
  serviceName: string;
  clientEmail: string;
  notice?: string;           // the no-show policy notice to show the client
  metadata?: Record<string, string>;
  onSuccess: (paymentIntentId: string) => void;
  onClose: () => void;
}

const money = (n: number) => `$${n.toFixed(2)}`;

const PayForm: React.FC<{ total: number; clientEmail: string; onSuccess: (id: string) => void; onClose: () => void }> = ({ total, clientEmail, onSuccess, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError(null);
    try {
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: { receipt_email: clientEmail || undefined },
      });
      if (confirmError) throw new Error(confirmError.message || 'Payment was declined.');
      if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
        toast.success(`Paid ${money(total)} — confirming your booking…`);
        onSuccess(paymentIntent.id);
      } else {
        throw new Error('Payment did not complete. You were not charged.');
      }
    } catch (err: any) {
      setError(err?.message || 'Payment failed');
      toast.error(err?.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handlePay}>
      <div className="min-h-[40px]">
        <PaymentElement
          onReady={() => setReady(true)}
          options={{ layout: 'tabs', wallets: { applePay: 'auto', googlePay: 'auto' }, defaultValues: { billingDetails: { email: clientEmail || undefined } } }}
        />
      </div>

      {error && <p className="text-red-600 text-sm mt-3">{error}</p>}

      <div className="flex items-center gap-2 text-xs text-gray-500 mt-3">
        <Shield className="w-4 h-4 shrink-0" />
        <span>Charged securely by Stripe. We never see your full card number.</span>
      </div>

      <div className="sticky bottom-0 bg-white pt-3 mt-4 -mx-5 px-5 pb-1 border-t border-gray-100 flex gap-3">
        <button type="button" onClick={onClose} disabled={loading} className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-50">
          Cancel
        </button>
        <button type="submit" disabled={!stripe || !ready || loading} className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
          {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Processing…</> : <><CreditCard className="w-5 h-5" /> Pay {money(total)}</>}
        </button>
      </div>
    </form>
  );
};

export default function PrepaymentModal({ amount, serviceName, clientEmail, notice, metadata, onSuccess, onClose }: Props) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${FUNCTIONS_URL}/createPaymentIntent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: Math.round(amount * 100),
            currency: 'usd',
            description: `Prepaid appointment — ${serviceName}`,
            receipt_email: clientEmail || undefined,
            metadata: { kind: 'noshow_prepay', source: 'public-booking', ...(metadata || {}) },
          }),
        });
        if (!resp.ok) throw new Error('Could not start payment. Please try again.');
        const data = await resp.json();
        if (!data.clientSecret) throw new Error('Payment could not be initialized.');
        if (!cancelled) setClientSecret(data.clientSecret);
      } catch (err: any) {
        if (!cancelled) setInitError(err?.message || 'Could not start payment.');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = useMemo(
    () => (clientSecret ? { clientSecret, appearance: { theme: 'stripe' as const, variables: { colorPrimary: '#7c3aed' } } } : null),
    [clientSecret]
  );

  return (
    <div className="fixed inset-0 z-[10020] bg-black/60 flex items-center justify-center p-3" style={{ backdropFilter: 'blur(4px)' }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        <div className="shrink-0 flex items-center justify-between px-5 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
          <div>
            <h3 className="text-lg font-bold">Prepay to confirm your appointment</h3>
            <p className="text-sm text-white/80">Paid in full upfront to keep your booking.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/15"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {notice && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex gap-2 text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{notice}</span>
            </div>
          )}

          {/* Price summary */}
          <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 space-y-1.5 text-sm">
            <div className="flex justify-between text-gray-700"><span>{serviceName}</span><span>{money(amount)}</span></div>
            <div className="flex justify-between pt-1.5 mt-1.5 border-t border-gray-200 font-bold text-gray-900 text-base"><span>Total due now</span><span>{money(amount)}</span></div>
          </div>

          {initError ? (
            <div className="text-center py-6">
              <p className="text-red-600 text-sm mb-3">{initError}</p>
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-semibold">Close</button>
            </div>
          ) : !options ? (
            <div className="flex items-center justify-center py-10 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Setting up secure payment…</div>
          ) : (
            <Elements stripe={stripePromise} options={options}>
              <PayForm total={amount} clientEmail={clientEmail} onSuccess={onSuccess} onClose={onClose} />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
}
